<script lang="ts" setup>
import type { FocusTrap } from 'focus-trap'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { useElementSize, useEventListener, useTimeoutFn, useWindowSize } from '@vueuse/core'
import { preventWindowScroll } from '@shared/composables'
import GIconButton from '@consumer/components/GIconButton.vue'
import ConciergeRecommendations
  from '@consumer/modules/myAccount/shared/recommendations/ConciergeRecommendations.vue'
import ConciergeInputVariant, { type ConciergeInputVariantName } from './ConciergeInputVariant.vue'
import { useConciergeState } from './conciergeState'
import { provideConciergeTakeover } from './useConciergeTakeover'
import { trackConciergeEvent } from './conciergeEventTracking'
import { useConciergeFunnelTracking } from './useConciergeFunnelTracking'

// The input lives inline on the homepage. It expands into a full-screen takeover
// when the buyer is focused in a field that has enough to fetch results — the SAME
// input teleports across (no re-mount, no lost keystrokes). Engagement is driven by
// focus + resultability rather than typing, so refocusing an already-full field
// re-expands without a keystroke. Once open, editing the field down (even to empty)
// keeps it open — the buyer is most likely retyping; only an explicit dismiss or a
// click outside the field while it's empty drops back to the homepage.
// Which input UI to render. Defaults to the free-text variant so nothing changes
// unless a variant is explicitly threaded in (query-param override today, split
// assignment once the UI experiment is registered). Both variants write the same
// store, so everything below here — engagement, teleport, results — is identical.
withDefaults(defineProps<{
  variant?: ConciergeInputVariantName
  // The server's calendar day, for the taps input's seasonal ordering (see BuyersConcierge).
  referenceDate?: Date
}>(), { variant: 'free_text', })

const engaged = ref(false)
const takeover = provideConciergeTakeover(engaged)

useConciergeFunnelTracking(() => __props.variant)

const panel = ref<HTMLElement | null>(null)
const inner = ref<HTMLElement | null>(null)
const inputWrap = ref<HTMLElement | null>(null)

// Engaged, the input rides pinned at the top of the scrolling results — unless it is tall
// enough to crowd them out on a phone (the madlibs and taps panels can be most of the
// screen), in which case it scrolls away like any other content.
const { height: inputHeight } = useElementSize(inputWrap)
const { height: viewportHeight } = useWindowSize()
const pinnable = computed(() => inputHeight.value < viewportHeight.value * 0.4)

// Where the buyer was in the results when the takeover last closed. Reopening — from the
// input or "See all" — lands there again, with the input pinned above the results. Forgotten
// once the prompt is cleared, since those results are gone.
let lastScrollTop = 0

const state = useConciergeState()
watch(() => state.raw, (raw) => {
  if (!raw.trim()) lastScrollTop = 0
})

// True only for the tick an explicit dismiss (✕/Escape) is collapsing, so the focus
// handler drops focus instead of restoring it — restoring would immediately re-open
// a still-ready field.
let dismissing = false

// Open while the buyer is focused in a resultable field. Editing it down — even to
// empty — never closes it: being kicked back to the homepage mid-edit is jarring when
// the buyer is about to retype. Only an explicit dismiss or a click outside the field
// while it's empty closes it (see collapse / onSurfaceClick). Focus leaving doesn't
// close it either — the teleport briefly blurs the input mid-move, and closing on that
// would bounce the takeover straight shut.
//
// Deferred a task, not a tick: a click focuses the input BEFORE the browser places the
// caret where it landed, and Vue flushes in between. Engaging in that gap teleports the
// input away and restores a caret the click hadn't set yet (it reads as 0 — the start of
// the text), so let the click finish first, then re-check.
const { start: scheduleEngage } = useTimeoutFn(() => {
  if (takeover.shouldEngage.value) engaged.value = true
}, 0, { immediate: false })

watch(takeover.shouldEngage, (should) => {
  if (should) scheduleEngage()
})

// An input that empties itself out (a brief with its last detail removed) closes as a
// readiness collapse, not a dismiss: tracked as such, and focus is restored rather than dropped.
watch(takeover.shouldCollapse, (should) => {
  if (should && engaged.value) engaged.value = false
})

function collapse () {
  // No-op when already collapsed: otherwise a stray Escape sets `dismissing` without
  // the engaged watcher ever running to clear it, and it would leak into the next
  // readiness-collapse (blurring instead of restoring caret).
  if (!engaged.value) return
  dismissing = true
  engaged.value = false
}

// Escape closes from anywhere, not only from inside the input: a backdrop click while the
// query still has text keeps the takeover open but blurs the input, and the next Escape
// then fires on <body>, where a handler on the surface never hears it. Escape inside some
// other open dialog is left to that dialog.
useEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return
  const target = event.target
  if (target instanceof Node && target !== document.body && !panel.value?.contains(target)) return
  collapse()
})

// A click on the backdrop — outside the input+results column — while the input reports itself
// dismissable means "I'm done": collapse. Clicks inside the column (retyping, refining a chip,
// paging results) never do.
function onSurfaceClick (event: MouseEvent) {
  if (!engaged.value || !takeover.dismissOnOutsideClick.value) return
  if (event.target instanceof Element && event.target.closest('.concierge-surface__column')) return
  collapse()
}

// Load focus-trap lazily: it (and tabbable) are only needed once the takeover opens,
// so keeping them out of the statically-bundled island leaves the landing-page payload
// unchanged at rest — and for all of control while the experiment sits at weight 0.
let trap: FocusTrap | null = null
async function activateTrap () {
  if (!panel.value) return
  if (!trap) {
    const { createFocusTrap } = await import('focus-trap')
    if (!panel.value) return
    // We drive focus/blur ourselves (see the engaged watch); the trap must not set
    // initial focus or return focus on deactivate, or dismissing a still-ready field
    // would re-focus the input and immediately re-open the takeover.
    trap = createFocusTrap(panel.value, {
      initialFocus: false,
      escapeDeactivates: false,
      returnFocusOnDeactivate: false,
      allowOutsideClick: true,
    })
  }
  trap.activate()
}

// iOS-safe scroll lock, reactive to `engaged` (the repo primitive GModal et al. use;
// it tears itself down on unmount).
preventWindowScroll({ when: () => engaged.value })

// The input the buyer is actually in — captured generically rather than by id,
// because the madlibs variant is four inputs and the free-text variant one. The
// teleport moves nodes without re-mounting them, so the reference stays valid on
// the other side.
function focusedInput (): HTMLInputElement | null {
  const active = document.activeElement
  return active instanceof HTMLInputElement && panel.value?.contains(active) ? active : null
}

const MOVE_MS = 380

// True while the content glides home after a collapse. The surface is back in the page by
// then, at its resting paint order, where the hero (a Swiper, which layers itself up)
// draws over it — so the glide keeps the dialog layer until it lands.
const collapsing = ref(false)

function prefersReducedMotion (): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// FLIP between the content's two homes: `first` is its rect before the teleport,
// measured pre-patch, and the glide runs from there to wherever it just landed — up
// from the homepage slot into the takeover on open, back into the slot on collapse.
// The white backdrop fades on its own (see the template), in step with the glide.
function animateMove (first: DOMRect): Animation | null {
  const content = inner.value
  if (!content || typeof content.animate !== 'function' || prefersReducedMotion()) return null

  const last = content.getBoundingClientRect()
  const dx = first.left - last.left
  const dy = first.top - last.top
  if (!dx && !dy) return null
  return content.animate(
    [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
    { duration: MOVE_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  )
}

// Teleporting re-parents the input, which blurs it. On open and on a readiness-driven
// collapse, restore focus + the buyer's exact caret to the SAME field they were in, so
// clicking a blank on the homepage lands them typing in that blank inside the takeover.
// On an explicit dismiss, drop focus so a still-ready field doesn't immediately re-open.
watch(engaged, async (open) => {
  const fromSeeAll = open && takeover.consumeOpenRequest()
  if (open) {
    trackConciergeEvent('takeover_engage', { variant: __props.variant, metadata: { method: fromSeeAll ? 'see_all' : 'input' } })
  }
  else {
    lastScrollTop = panel.value?.scrollTop ?? 0
    trackConciergeEvent('takeover_collapse', { variant: __props.variant, metadata: { method: dismissing ? 'dismiss' : 'readiness' } })
  }

  const input = focusedInput()
  const start = input?.selectionStart ?? null
  const end = input?.selectionEnd ?? null
  const firstRect = inner.value?.getBoundingClientRect() ?? null

  await nextTick()

  if (open) {
    // Back to where the buyer left the results — only while the input can stay pinned above
    // them (see `pinnable`); a panel too tall to pin opens at the top so the field stays on
    // screen. Focus must not scroll it back up, and the glide is skipped: from that depth
    // the content would fly up past the viewport.
    const scrollBack = pinnable.value ? lastScrollTop : 0
    if (scrollBack && panel.value) panel.value.scrollTop = scrollBack
    const restored = scrollBack > 0
    // Restore focus + caret before awaiting the lazy trap, so the teleport doesn't
    // leave the input blurred while the focus-trap chunk loads on first open. A "See
    // all" open had no field focused: give it the first one, as a click would.
    if (input) {
      input.focus({ preventScroll: restored })
      if (start !== null && end !== null) input.setSelectionRange(start, end)
    }
    else if (fromSeeAll) {
      inputWrap.value?.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: restored })
    }
    if (firstRect && !restored) animateMove(firstRect)
    await activateTrap()
    return
  }

  trap?.deactivate()
  const wasDismiss = dismissing
  dismissing = false
  if (wasDismiss) {
    if (input && document.activeElement === input) input.blur()
  }
  else if (input) {
    input.focus()
    if (start !== null && end !== null) input.setSelectionRange(start, end)
  }
  const glide = firstRect ? animateMove(firstRect) : null
  if (glide) {
    collapsing.value = true
    glide.onfinish = glide.oncancel = () => { collapsing.value = false }
  }
})

onBeforeUnmount(() => trap?.deactivate())
</script>

<template>
  <div class="concierge-host">
    <Transition name="concierge-backdrop">
      <div v-if="engaged" class="concierge-backdrop z-dialog"/>
    </Transition>

    <Teleport to="body" :disabled="!engaged">
      <div
        ref="panel"
        class="concierge-surface"
        :class="[
          // hide-window-scrollbar: the page can't scroll under the takeover, so scrolling the
          // results to their end never chains through to it.
          {
            'concierge-surface--engaged': engaged,
            'concierge-surface--collapsing': collapsing,
            'hide-window-scrollbar': engaged,
          },
          engaged || collapsing ? 'z-dialog' : '',
        ]"
        :role="engaged ? 'dialog' : undefined"
        :aria-modal="engaged ? 'true' : undefined"
        :aria-label="engaged ? 'Gift concierge' : undefined"
        @click="onSurfaceClick"
      >
        <GIconButton
          v-if="engaged"
          class="concierge-surface__close z-dialog-button"
          :icon="faClose"
          size="medium"
          label="Close gift concierge"
          name="close-buyers-concierge"
          @click="collapse"
        />

        <div ref="inner" class="concierge-surface__inner">
          <div class="concierge-surface__column">
            <div
              ref="inputWrap"
              class="concierge-surface__input"
              :class="{ 'pinned z-sticky': engaged && pinnable }"
            >
              <ConciergeInputVariant :variant="variant" :referenceDate="referenceDate"/>
            </div>

            <ConciergeRecommendations :variant="variant"/>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.concierge-host {
  width: 100%;
}

// At rest the surface is a transparent block: the input renders exactly where the page
// places it, no extra chrome. `__inner` stays the landing container's lg width (1240px) in
// both states so the FLIP below measures a constant box; the column inside it decides how
// wide the input and results actually sit.
.concierge-surface__inner {
  margin: 0 auto;
  max-width: 1240px;
  width: 100%;
}

// At rest the input keeps a centred 720px column while the results run the full landing width
// — the same grid and card size as engaged, so opening doesn't reflow them. Capping the input
// — not `__inner` — is what keeps the engage/collapse FLIP (which translates `__inner`) a pure
// translate with no sideways lurch.
.concierge-surface:not(.concierge-surface--engaged) .concierge-surface__input {
  margin-inline: auto;
  max-width: 720px;
}

// Breathing room above the input inside the pinned bar; it also keeps the fixed ✕ clear of the
// field. Carried by the wrapper whether or not it ends up pinned, so the input rests at the same
// height either way — which is why `__inner` gives the same amount back.
$pinned-bar-padding: 50px;

// Engaged: full-screen takeover covering everything, the same input now with room
// below for results. The white is `.concierge-backdrop`, a separate fixed layer, so it
// can still be fading out after this surface has returned to the page on collapse.
// z-layering comes from the z-dialog utility.
.concierge-surface--engaged {
  inset: 0;

  // x hidden, not visible: the pinned input below bleeds past the padding to the edges.
  overflow: hidden auto;

  // The top spacing lives on `__inner` and the input wrapper, not here: a scroll container's
  // own padding is where its pinned children stop, which would leave a strip above the input
  // for cards to show through.
  padding: 0 20px 48px;
  position: fixed;

  .concierge-surface__inner {
    padding-top: calc(64px - #{$pinned-bar-padding} + 4vh);
  }

  .concierge-surface__input {
    padding-top: $pinned-bar-padding;
  }
}

// Positioned so the z-dialog utility applies for the collapse glide (see `collapsing`);
// at rest the surface is a plain block again.
.concierge-surface--collapsing {
  position: relative;
}

// Pinned to the top of the scrolling results while engaged (see `pinnable`), painted in
// the backdrop's white so the cards pass underneath. Full-bleed, content still aligned to the
// column: the outer cards' image shadows reach into the gutters beside the column, and a bar
// only as wide as the column would let them peek out on either side.
.concierge-surface--engaged .concierge-surface__input.pinned {
  background: var(--color-bg-surface-white);
  margin-inline: calc(50% - 50vw);
  padding-bottom: 8px;
  padding-inline: calc(50vw - 50%);
  position: sticky;
  top: 0;
}

// Fades in under the entry glide and out over the collapse glide, so the page fades
// back in as the input returns to its slot instead of snapping.
.concierge-backdrop {
  background: var(--color-bg-surface-white);
  inset: 0;
  position: fixed;
}

.concierge-backdrop-enter-active {
  transition: opacity 0.24s ease-out;
}

.concierge-backdrop-leave-active {
  transition: opacity 0.38s ease-out;
}

.concierge-backdrop-enter-from,
.concierge-backdrop-leave-to {
  opacity: 0;
}

// Pinned to the viewport so it stays reachable while the surface scrolls. A sibling of
// `__inner`, not a child: the glide translates `__inner`, and a transformed ancestor
// would pin the button to that column instead — it would ride the glide, then jump to
// the corner once the transform cleared. Fades in alongside the glide instead of popping.
.concierge-surface__close {
  animation: concierge-close-fade 0.38s ease-out;
  position: fixed;
  right: 20px;
  top: 20px;
}

@keyframes concierge-close-fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .concierge-surface__close {
    animation: none;
  }

  .concierge-backdrop-enter-active,
  .concierge-backdrop-leave-active {
    transition: none;
  }
}

@media (min-width: 1024px) {
  .concierge-surface--engaged .concierge-surface__inner {
    // Sit the input in the upper third rather than dead-center, leaving the lower
    // two-thirds as the results canvas.
    padding-top: calc(64px - #{$pinned-bar-padding} + 12vh);
  }
}
</style>
